import { useEffect, useMemo, useRef, useState } from 'react';
import { MessageCircle, Loader2, Send, Search, Check, X, Phone, UserPlus } from 'lucide-react';
import { formatTelefoneBR, formatDataSP, formatHoraSP, parseSPDate } from '@fiodecobre/shared';
import { api } from '../../lib/api';
import { useToast } from '../Toast';
import { useAlertModal } from '../AlertModalProvider';
import { useListaTeclado } from '../../lib/useListaTeclado';
import { enviarParaSelecionados, resumoFalhas, type FalhasEnvio } from '../whatsapp/ModalEnviarWhatsAppCliente';
import { pdfBase64ToJpegs } from '../../utils/pdfToJpeg';

interface Props {
  orcamentoId: number | null;
  numero?: string;
  clienteId?: number;
  clienteNome?: string;
  // funcao que gera o PDF e devolve { base64, filename }. Chamada ao Enviar.
  // Opcional quando `imagensProntas` vem preenchido (pula a geracao interna do PDF do orcamento).
  obterPdfBase64?: () => Promise<{ base64: string; filename: string }>;
  // Imagens JPG base64 (sem prefixo data:) JA prontas — pula a conversao PDF->JPEG e o
  // `obterPdfBase64`. Uso: extrato de parcelamento do cartao (ModalFinalizarVenda), que gera a
  // propria imagem e so precisa do MESMO cano de contato+envio deste modal.
  imagensProntas?: string[];
  // Nome do arquivo quando usando `imagensProntas` (senao usa o filename devolvido por obterPdfBase64).
  filenamePronto?: string;
  // Legenda customizada da mensagem. Sem ela, usa "Orcamento {numero}" (comportamento original).
  captionPersonalizado?: string;
  // A imagem pronta e' o FLY do orcamento (07/10/2026): muda o rotulo da previa e faz o servidor
  // consultar o NAO QUERO antes de mandar — fly e' oferta, o PDF do orcamento nao.
  fly?: boolean;
  onClose: (enviouAlgum?: boolean) => void;
}

interface ContatoSugestao {
  id: number;
  nome: string;
  push_name: string | null;
  telefone: string | null;
  jid: string;
  foto_url: string | null;
}

// Uma conversa recente da inbox atendimento (GET /api/conversas/recentes).
interface ConversaRecente {
  contato_id: number;
  jid: string;
  nome: string;
  push_name: string | null;
  telefone: string;
  foto_url: string | null;
  ultima_mensagem_em: string;
}

// Linha escolhivel da lista. Junta as tres procedencias (numero do cadastro do cliente,
// conversa recente, resultado de busca) numa forma so — e o teclado navega por ESSA lista,
// nao por tres.
interface Destino {
  chave: string;
  nome: string;
  digitos: string;           // so digitos, e o que vai pro backend
  foto_url: string | null;
  rotulo: string | null;     // "WhatsApp do cadastro" / "Fixo — nao recebe WhatsApp"
  quando: string | null;     // ISO da ultima mensagem (so nas recentes)
}

// Formata pra exibicao. NAO reimplementar: o `formatTelefoneBR` do @fiodecobre/shared tira o
// DDI 55 e devolve "(64) 3671-2885". A versao caseira que morava aqui assumia que TODO numero
// vinha com DDI, entao um fixo de 10 digitos do cadastro (6436712885) saia como "+64 36 7128-85",
// lendo o DDD 64 como pais. O envio nao quebrava (o backend prefixa o 55 quando falta), mas o
// campo mostrava um numero irreconhecivel. (2026-08-06)
function exibirTelefone(v: string | null | undefined): string {
  return formatTelefoneBR(v);
}

// Celular BR tem 11 digitos (DDD + 9 + 8). Menos que isso e fixo — e fixo nao recebe WhatsApp,
// entao a lista avisa em vez de deixar o operador descobrir pelo silencio do cliente.
function ehFixo(digitos: string): boolean {
  const d = digitos.replace(/^55/, '');
  return d.length > 0 && d.length <= 10;
}

// "22:32" pra hoje, "ontem", "05/08" pra antes — a lista e de RECENTES, entao a data cheia
// so ocupa espaco. Carimbo sempre pelos helpers SP (nunca `new Date` cru, ver DA-330).
function quandoCurto(iso: string | null): string {
  if (!iso) return '';
  const data = formatDataSP(iso);
  const hoje = formatDataSP(new Date());
  if (data === hoje) return formatHoraSP(iso);
  const ontem = formatDataSP(new Date(Date.now() - 86400000));
  if (data === ontem) return 'ontem';
  return data.slice(0, 5); // DD/MM
}

// True se o texto parece ser um numero de telefone (so tem digitos / +/espaco/-/parenteses)
function pareceTelefone(v: string): boolean {
  if (!v) return false;
  const apenasFone = /^[\d\s+\-()]+$/.test(v);
  const digitos = v.replace(/\D/g, '');
  return apenasFone && digitos.length >= 3;
}

export function ModalEnviarWhatsApp({ orcamentoId, numero, clienteId, clienteNome, obterPdfBase64, imagensProntas, filenamePronto, captionPersonalizado, fly, onClose }: Props) {
  const toast = useToast();
  const { alerta } = useAlertModal();
  const [texto, setTexto] = useState('');
  // Quem vai receber. Ate 2026-08-07 era UM destino (`whatsappFinal`) escolhido por clique, e a
  // linha escolhida ficava amarela — o mesmo amarelo do cursor do teclado, que nasce na linha 0.
  // Resultado: o modal abria com uma linha amarela que o operador lia como "ja vai pra este", sem
  // nunca ter escolhido ninguem. Agora quem manda e a caixa de marcacao (nada marcado = nada sai,
  // DA-347) e o amarelo passou a significar so uma coisa: e o WhatsApp do cadastro do cliente.
  // Guarda o `Destino` inteiro, nao so os digitos, pra lista de marcados mostrar nome e nao sumir
  // quando a busca troca a lista debaixo.
  const [escolhidos, setEscolhidos] = useState<Destino[]>([]);
  const [falhas, setFalhas] = useState<FalhasEnvio>(new Map());
  const [enviando, setEnviando] = useState(false);
  // Previa da simulacao aberta em tamanho real (so quando ha imagem pronta — ver o bloco da
  // coluna esquerda). Na coluna ela cabe em ~284px de largura; aqui ela aparece como o cliente
  // vai receber.
  const [ampliada, setAmpliada] = useState(false);
  // Bloco "Contato novo" (08/09/2026): telefone + nome, pra o contato nascer COM nome.
  const [novoTel, setNovoTel] = useState('');
  const [novoNome, setNovoNome] = useState('');
  const [checando, setChecando] = useState(false);
  const [check, setCheck] = useState<{ existe: boolean; incerto: boolean } | null>(null);
  const [incluindo, setIncluindo] = useState(false);
  const [sugestoes, setSugestoes] = useState<ContatoSugestao[]>([]);
  const [buscando, setBuscando] = useState(false);
  const [recentes, setRecentes] = useState<ConversaRecente[]>([]);
  const [carregandoRecentes, setCarregandoRecentes] = useState(true);
  const [numerosCliente, setNumerosCliente] = useState<Destino[]>([]);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Os numeros do CADASTRO do cliente. Ate 2026-08-06 isso ia escrito direto no campo de busca —
  // o que preenchia a tela e escondia qualquer lista. Agora vira a 1a secao da lista (decisao do
  // Ruan): o atalho continua a um clique e o campo fica livre pras conversas recentes aparecerem.
  useEffect(() => {
    if (!clienteId || clienteId === 1) return;
    let cancelado = false;
    api.get<{ whatsapp?: string | null; telefone?: string | null }>(`/api/clientes/${clienteId}`)
      .then((c) => {
        if (cancelado) return;
        const vistos = new Set<string>();
        const linhas: Destino[] = [];
        const juntar = (valor: string | null | undefined, rotulo: string) => {
          const d = (valor ?? '').replace(/\D/g, '');
          if (!d || vistos.has(d)) return;
          vistos.add(d);
          linhas.push({
            chave: `cli-${d}`,
            nome: clienteNome || 'Cliente do orcamento',
            digitos: d,
            foto_url: null,
            rotulo: ehFixo(d) ? 'Fixo — nao recebe WhatsApp' : rotulo,
            quando: null,
          });
        };
        juntar(c.whatsapp, 'WhatsApp do cadastro');
        juntar(c.telefone, 'Telefone do cadastro');
        setNumerosCliente(linhas);
      })
      .catch(() => {});
    return () => { cancelado = true; };
  }, [clienteId, clienteNome]);

  // Quem falou com a loja por ultimo. Carrega uma vez, na abertura — o modal e curto e a lista
  // nao precisa reagir a nada. Falha aqui nao atrapalha: o campo de busca segue funcionando.
  useEffect(() => {
    let cancelado = false;
    api.get<ConversaRecente[]>('/api/conversas/recentes?inbox=atendimento&limit=30')
      .then((r) => { if (!cancelado) setRecentes(r); })
      .catch(() => { if (!cancelado) setRecentes([]); })
      .finally(() => { if (!cancelado) setCarregandoRecentes(false); });
    return () => { cancelado = true; };
  }, []);

  // Dedup por telefone (mesmo numero pode ter 2 contatos: 1 por inbox atendimento/compras).
  function dedupPorTelefone(lista: ContatoSugestao[]): ContatoSugestao[] {
    const porDigits = new Map<string, ContatoSugestao>();
    for (const c of lista) {
      const digits = (c.telefone ?? '').replace(/\D/g, '');
      if (!digits) continue;
      const existente = porDigits.get(digits);
      if (!existente) { porDigits.set(digits, c); continue; }
      const novoMelhor = (!!c.foto_url && !existente.foto_url) || (!!c.push_name && !existente.push_name);
      if (novoMelhor) porDigits.set(digits, c);
    }
    return [...porDigits.values()];
  }

  // Busca contatos por nome com debounce. Se o texto parece telefone, nao busca.
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!texto || texto.length < 2 || pareceTelefone(texto)) {
      setSugestoes([]);
      return;
    }
    debounceRef.current = setTimeout(async () => {
      setBuscando(true);
      try {
        // Orcamento e pra cliente — busca so contatos da inbox atendimento (oculta os de compras/fornecedores).
        const lista = await api.get<ContatoSugestao[]>(`/api/contatos?busca=${encodeURIComponent(texto)}&sem_grupo=true&inbox=atendimento&limit=20`);
        setSugestoes(dedupPorTelefone(lista.filter((c) => c.telefone)));
      } catch {
        setSugestoes([]);
      } finally {
        setBuscando(false);
      }
    }, 250);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [texto]);

  // Chave que casa o MESMO numero escrito de jeitos diferentes (com/sem DDI 55, com/sem o 9o
  // digito). Sem ela o numero do cadastro apareceria de novo logo abaixo, nas recentes.
  function chaveTel(digitos: string): string {
    let d = digitos.replace(/\D/g, '');
    if (d.startsWith('55') && d.length >= 12) d = d.slice(2);
    if (d.length === 11 && d[2] === '9') d = d.slice(0, 2) + d.slice(3);
    return d;
  }

  // A lista que aparece na tela — e a MESMA que o teclado navega. Campo vazio mostra o cadastro
  // do cliente + as recentes; digitou nome, mostra a busca; digitou numero, nao ha o que listar.
  const listaVisivel = useMemo<Destino[]>(() => {
    // Numero digitado a mao tambem e uma LINHA com caixa de marcacao — nao um aviso solto. Assim
    // "nada sai sem estar marcado" vale pro numero digitado igual vale pro contato da lista, e o
    // teclado (setas + Enter) alcanca ele pelo mesmo caminho.
    if (pareceTelefone(texto)) {
      const d = texto.replace(/\D/g, '');
      return [{
        chave: `digitado-${d}`,
        nome: exibirTelefone(texto),
        digitos: d,
        foto_url: null,
        rotulo: d.length < 10
          ? 'Numero incompleto — digite com DDD'
          : ehFixo(d) ? 'Numero digitado — parece um fixo, confira' : 'Numero digitado',
        quando: null,
      }];
    }
    if (texto.trim().length >= 2) {
      return sugestoes.map((c) => ({
        chave: `sug-${c.id}`,
        nome: c.nome || c.push_name || c.telefone || '?',
        digitos: (c.telefone ?? '').replace(/\D/g, ''),
        foto_url: c.foto_url,
        rotulo: null,
        quando: null,
      }));
    }
    const vistos = new Set(numerosCliente.map((n) => chaveTel(n.digitos)));
    const recentesFiltradas = recentes
      .filter((r) => {
        const k = chaveTel(r.telefone);
        if (!k || vistos.has(k)) return false;
        vistos.add(k);
        return true;
      })
      .map<Destino>((r) => ({
        chave: `rec-${r.contato_id}`,
        nome: r.nome || r.push_name || r.telefone,
        digitos: r.telefone.replace(/\D/g, ''),
        foto_url: r.foto_url,
        rotulo: null,
        quando: r.ultima_mensagem_em,
      }));
    return [...numerosCliente, ...recentesFiltradas];
  }, [texto, sugestoes, numerosCliente, recentes]);

  // Comparacao pelo MESMO `chaveTel` que dedupa a lista — o mesmo contato pode aparecer com DDI 55
  // na busca e sem ele nas recentes; comparando digito cru ele nasceria desmarcado na outra vista e
  // daria pra marcar duas vezes, mandando o orcamento duplicado pra mesma pessoa.
  const marcados = useMemo(() => new Set(escolhidos.map((e) => chaveTel(e.digitos))), [escolhidos]);

  // Marca/desmarca. O campo de busca NAO e limpo: quem marcou alguem num resultado de busca
  // continua vendo o resultado (e o visto na caixa). Quem sai da lista visivel segue visivel na
  // faixa de marcados logo acima dela — selecao que some da tela e selecao que ninguem confere.
  function alternar(d: Destino) {
    if (d.digitos.length < 10) return;
    const k = chaveTel(d.digitos);
    setFalhas(new Map());
    setEscolhidos((atual) => atual.some((x) => chaveTel(x.digitos) === k)
      ? atual.filter((x) => chaveTel(x.digitos) !== k)
      : [...atual, d]);
  }

  const teclado = useListaTeclado(listaVisivel, alternar);

  /** Pergunta ao proprio WhatsApp se o numero existe. Nunca trava nada — so informa. */
  async function checarNumero() {
    const d = novoTel.replace(/\D/g, '');
    if (d.length < 10) { setCheck(null); return; }
    setChecando(true);
    try {
      const r = await api.get<{ existe: boolean; incerto?: boolean }>(
        `/api/contatos/checar-numero?telefone=${encodeURIComponent(d)}&inbox=atendimento`,
      );
      setCheck({ existe: !!r.existe, incerto: !!r.incerto });
    } catch {
      // Falha de rede nao acusa numero ruim: le como "nao deu pra conferir".
      setCheck({ existe: true, incerto: true });
    } finally {
      setChecando(false);
    }
  }

  /**
   * Cria o contato COM o nome e ja o marca pra receber.
   *
   * ⛔ **Passou a usar `capturar-contato` em 09/09/2026 ([DA-697]), e o motivo e' um defeito real:**
   * antes chamava `POST /api/conversas`, que exige admin/gerente ([DA-027] — vendedora nao inicia
   * conversa outbound). CRISTINA, LANNA e JOAO sao `vendedor`/`estoquista`, entao pra QUEM VENDE
   * este botao devolvia **403** — o recurso subiu em 08/09 e nunca funcionou pra elas.
   * A rota nova acha/cria o contato pela MESMA peca (`garantirContato`: DDI, variantes BR do 9o
   * digito, jid real pelo WhatsApp) e **nao abre conversa** — e a regra da DA-027 segue inteira,
   * porque ela protege puxar assunto com o cliente, nao anotar de quem e' o numero.
   * ⚠️ O envio nao depende disto: a rota de envio manda pelo NUMERO. Este bloco existe pro NOME.
   */
  async function incluirNovo(): Promise<boolean> {
    const d = novoTel.replace(/\D/g, '');
    if (d.length < 10) return false;
    setIncluindo(true);
    try {
      const r = await api.post<{ contato_id: number; contato_nome: string | null }>(
        `/api/orcamentos/${orcamentoId}/capturar-contato`,
        { telefone: d, nome: novoNome.trim() || null },
      );
      alternar({
        chave: `novo-${d}`,
        nome: r?.contato_nome || novoNome.trim() || exibirTelefone(d),
        digitos: d,
        foto_url: null,
        rotulo: 'Contato novo',
        quando: null,
      });
      setNovoTel('');
      setNovoNome('');
      setCheck(null);
      return true;
    } catch (e: any) {
      alerta(e?.corpo?.erro ?? e?.message ?? 'Nao foi possivel incluir o contato.', { titulo: 'Falha ao incluir', tipo: 'erro' });
      return false;
    } finally {
      setIncluindo(false);
    }
  }

  function onChangeTexto(v: string) {
    setTexto(v);
    // Numero digitado que MUDA descarta a marcacao do numero anterior — senao o meio-digitado de
    // antes ficaria na fila de envio sem nenhuma linha na tela pra denunciar.
    const d = v.replace(/\D/g, '');
    setEscolhidos((atual) => atual.filter((x) => !x.chave.startsWith('digitado-') || x.digitos === d));
  }

  // `destinoDireto` existe pro duplo-clique na lista: manda SO pra aquela linha, sem depender do
  // que esta marcado (o clique duplo alterna a caixa duas vezes, entao o estado nem mudou — e ele
  // so existiria no render seguinte de qualquer jeito). O botao Enviar segue pelos marcados.
  async function enviar(destinoDireto?: string) {
    if (!orcamentoId) {
      alerta('Salve o orcamento antes de enviar pelo WhatsApp.', { titulo: 'Orcamento nao salvo', tipo: 'aviso' });
      return;
    }
    // ⛔ **Campo preenchido que so' valia se um SEGUNDO botao fosse apertado** — relato dele em
    // 09/09/2026: *"o usuario esta esquecendo de clicar para salvar o telefone do cliente"*. Quem
    // digitava telefone e nome no bloco "Contato novo" e clicava direto em Enviar perdia o que
    // digitou, **calado**: nenhum aviso, nenhum contato criado, e o envio saia pra quem estivesse
    // marcado (ou reclamava que ninguem estava). Nao e' indisciplina de quem opera, e' defeito de
    // desenho — o mesmo gesto partido em dois que a Agenda pagou em 08/09 ([DA-684]).
    // ✅ Agora o Enviar RECOLHE o que ficou no campo antes de decidir pra quem vai.
    // ⚠️ As duas decisoes saem ANTES de chamar `incluirNovo`: ele faz `setEscolhidos`, e ler
    // `escolhidos` depois devolveria a lista de antes do redesenho — por isso o numero recolhido
    // entra na lista de destinos aqui, na mao, em vez de ser lido de volta do estado.
    const pendente = novoTel.replace(/\D/g, '');
    const recolher = !destinoDireto
      && pendente.length >= 10
      && !escolhidos.some((e) => e.digitos === pendente);
    if (recolher) {
      const incluiu = await incluirNovo();
      // Falhou incluir: NAO seguir o envio calado — quem digitou aquele numero quis mandar pra ele.
      if (!incluiu) return;
    }
    const marcados = recolher
      ? [...escolhidos.map((e) => e.digitos), pendente]
      : escolhidos.map((e) => e.digitos);
    const destinos = destinoDireto ? [destinoDireto.replace(/\D/g, '')] : marcados;
    if (destinos.length === 0 || destinos.some((d) => d.length < 10)) {
      alerta('Marque quem vai receber (ou digite um numero com DDD e marque ele).', { titulo: 'Ninguem marcado', tipo: 'aviso' });
      return;
    }
    setEnviando(true);
    setFalhas(new Map());
    try {
      let imagens: string[];
      let filename: string;
      if (imagensProntas && imagensProntas.length > 0) {
        // Imagem(s) ja prontas (ex: extrato de parcelamento) — pula a geracao/conversao do PDF do orcamento.
        imagens = imagensProntas;
        filename = filenamePronto || 'documento.pdf';
      } else {
        if (!obterPdfBase64) throw new Error('Nada pra enviar');
        const gerado = await obterPdfBase64();
        // Converte cada pagina do PDF em JPG (cliente abre direto no preview do WhatsApp).
        imagens = await pdfBase64ToJpegs(gerado.base64);
        filename = gerado.filename;
      }
      const caption = captionPersonalizado ?? (numero ? `Orcamento ${numero}` : 'Orcamento');
      // Gera UMA vez e reusa em todos os envios; o endpoint aceita 1 jid por chamada (mesmo cano
      // que NF e Contas a Receber ja usam pra multiplos destinatarios).
      const { ok, falhas: f } = await enviarParaSelecionados(destinos, (digits) =>
        api.post(`/api/orcamentos/${orcamentoId}/enviar-whatsapp`, {
          jid: digits,
          imagens_base64: imagens,
          filename,
          caption,
          ...(fly ? { fly: true } : {}),
        }).then(() => undefined),
      );
      setFalhas(f);
      if (f.size === 0) {
        toast.sucesso(ok === 1 ? 'Enviado pelo WhatsApp' : `Enviado para ${ok} contatos`);
        onClose(true);
        return;
      }
      // Envio que falhou NAO some calado: o modal fica aberto, as linhas que falharam ficam
      // marcadas em vermelho e as que deram certo saem da fila — reenviar nao duplica ninguem.
      // (No duplo-clique nao ha fila a limpar: ele nao passou pelos marcados.)
      if (!destinoDireto) setEscolhidos((atual) => atual.filter((x) => f.has(x.digitos)));
      if (ok > 0) toast.aviso(`Enviado para ${ok} de ${ok + f.size}. ${resumoFalhas(f)}`);
      else alerta(resumoFalhas(f), { titulo: 'Erro no envio', tipo: 'erro' });
    } catch (err: any) {
      alerta(err?.message || 'Falha ao enviar pelo WhatsApp', { titulo: 'Erro no envio', tipo: 'erro' });
    } finally {
      setEnviando(false);
    }
  }

  // So mostra o preview quando a imagem ja vem pronta (ex: simulacao de parcelamento). No caminho do
  // PDF do orcamento nao ha imagem antes do Enviar, entao o modal continua enxuto como antes.
  const temPreview = !!(imagensProntas && imagensProntas.length > 0);

  return (
    // z-[1100]: pode abrir aninhado de DENTRO de um <Modal/> padrao (z-1000, ex: ModalFinalizarVenda) —
    // precisa renderizar POR CIMA dele. Aberto solto (OrcamentoNovo, sem Modal por baixo) fica igual.
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[1100]">
      {/* Com previa o modal vai a `max-w-4xl` e o corpo abre em DUAS COLUNAS (simulacao | quem
          recebe). Medido em 21/09/2026 na janela do Ruan (1536x752 CSS = 1920x1080 com zoom 125%):
          em coluna unica a imagem do extrato sozinha rendia 388x583 e o corpo pedia 1201px de
          conteudo pra 556 visiveis — EXCEDENTE de 645px, com NENHUM dos quatro blocos inteiro na
          tela. Pior: o botao Enviar so' destrava marcando alguem, e o campo que marca ficava fora
          da vista. Em duas colunas o excedente e' ZERO, e segue zero em tablet deitado (1024x600)
          e em pe (768x1024). A altura da imagem e' ELASTICA (78 + 34 + 58*parcelas + 40 + 112
          sobre 640 de largura, ver gerarExtratoParcelamento) — com 12 parcelas ela e' 640x960,
          proporcao 1,5, e por isso nenhum teto de largura resolve sozinho.
          ⛔ Sem previa (PDF do orcamento) NADA muda: segue `max-w-md` em coluna unica. */}
      {/* Fly (07/10/2026, pedido dele: "aumente a largura, pra exibir um fly maior"): o modal vai a
          1240px e a coluna da arte a 58%. A arte e' QUADRADA, entao quem a limita e' o menor entre
          a largura da coluna e a altura do corpo. Medido em 1746x940: com `max-w-6xl` + 55% a arte
          dava 520x520 com ~150px de altura sobrando — largura ainda mandava. */}
      <div
        className={`bg-white rounded-xl shadow-xl w-full ${fly ? '' : temPreview ? 'max-w-4xl' : 'max-w-md'} mx-4 flex flex-col max-h-[90vh]`}
        style={fly ? { maxWidth: 1240 } : undefined}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 shrink-0">
          <h3 className="text-lg font-bold text-gray-800 flex items-center gap-2">
            <MessageCircle size={20} className="text-green-600" />
            Enviar pelo WhatsApp
          </h3>
          <button type="button" className="btn-fechar-modal" onClick={() => onClose()} aria-label="Fechar">×</button>
        </div>

        <div className={`p-5 min-h-0 ${temPreview ? 'flex gap-4 overflow-hidden flex-1' : 'space-y-3 overflow-y-auto'}`}>
          {temPreview && (
            <div className="flex flex-col min-h-0" style={{ flex: fly ? '0 0 58%' : '0 0 40%' }}>
              <div className="flex items-baseline justify-between gap-2 mb-2">
                <label className="block text-xs font-semibold text-gray-600 uppercase">{fly ? 'Fly que o cliente vai receber' : 'Simulação a enviar'}</label>
                {/* Quem limita a previa e' a ALTURA da janela, nao a largura do modal: com 12
                    parcelas ela cabe em ~284px de largura (contra 388 na coluna unica de antes).
                    Legivel, mas menor — por isso a saida pra ver em tamanho real, que e' o que
                    o cliente vai receber. */}
                <button
                  type="button"
                  onClick={() => setAmpliada(true)}
                  className="text-[11px] text-blue-600 underline shrink-0"
                >
                  ver em tamanho real
                </button>
              </div>
              <div className="rounded-lg border border-gray-200 bg-gray-100 p-2 flex-1 min-h-0 flex items-start justify-center gap-2">
                {imagensProntas!.map((img, i) => (
                  <img
                    key={i}
                    src={`data:image/jpeg;base64,${img}`}
                    alt={`Simulação — página ${i + 1}`}
                    onClick={() => setAmpliada(true)}
                    className="max-h-full w-auto max-w-full object-contain rounded shadow-sm bg-white cursor-zoom-in"
                  />
                ))}
              </div>
            </div>
          )}
          {/* Coluna da direita (ou a coluna unica, sem previa). O `space-y-3` morava no corpo;
              com duas colunas ele desce pra ca, senao o gap entre as colunas viraria espaco
              vertical tambem. */}
          <div className={`space-y-3 min-h-0 ${temPreview ? 'flex flex-col flex-1 overflow-hidden' : ''}`}>
          {clienteNome && clienteNome !== 'Consumidor' && (
            <div className="text-sm text-gray-600">
              Cliente do orcamento: <strong>{clienteNome}</strong>
            </div>
          )}
          {/* ⚠️ CONTATO NOVO — pedido dele em 08/09/2026, com o print marcando este espaco, acima
              de "Marque quem vai receber", e o modal `ModalNovaConversa` do Atendimento como
              referencia de forma (*"como estes campos como no Atendimento"*): `Telefone *` +
              `Nome (opcional)`.
              ⛔ **O NOME e' o que este bloco existe pra capturar.** Digitar so' o numero ja
              funcionava — o campo de busca abaixo monta a linha "Numero digitado" desde antes —,
              e e' por isso que a casa tem 1.330 numeros e so' 47 sabem de quem sao (medido em
              08/09/2026). Sem nome o contato nasce anonimo e so' ganha rotulo se a pessoa
              responder, com o `push_name` que ELA escolheu. */}
          <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 space-y-2">
            <label className="block text-xs font-semibold text-gray-600 uppercase">
              Contato novo
            </label>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="block text-[11px] text-gray-500 mb-0.5">Telefone *</label>
                <div className="relative">
                  <Phone size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    type="tel"
                    value={novoTel}
                    onChange={(e) => { setNovoTel(e.target.value); setCheck(null); }}
                    onBlur={checarNumero}
                    placeholder="+55 64 9..."
                    disabled={incluindo}
                    className="w-full pl-7 pr-3 py-1.5 border border-gray-300 rounded outline-none focus:ring-2 focus:ring-green-500 text-sm"
                  />
                </div>
              </div>
              <div>
                <label className="block text-[11px] text-gray-500 mb-0.5">Nome (opcional)</label>
                {/* `no-upper`: nome de contato e' caixa livre, a mesma regra do `ModalNovaConversa`
                    e do `PainelContato` — sem a classe o `text-transform` global gravaria tudo
                    em maiusculo. */}
                <input
                  type="text"
                  value={novoNome}
                  onChange={(e) => setNovoNome(e.target.value)}
                  placeholder="Como aparecera na lista"
                  disabled={incluindo}
                  className="no-upper w-full px-2.5 py-1.5 border border-gray-300 rounded outline-none focus:ring-2 focus:ring-green-500 text-sm"
                />
              </div>
            </div>

            {/* ⚠️ O sinal so' acende DEPOIS da resposta chegar (`check && ...`), nunca por
                `!check?.existe` — que confunde "ainda nao perguntei" com "numero ruim" e pisca
                vermelho em todo numero bom enquanto se digita ([DA-649]).
                ⛔ E o estado `incerto` NAO vira recusa: com o WhatsApp desconectado ninguem tem
                como saber, e negar barraria envio legitimo. */}
            {check && (
              <div className="text-[11px] flex items-center gap-1.5" style={{
                color: check.incerto ? '#92400e' : check.existe ? '#166534' : '#991b1b',
              }}>
                {check.incerto
                  ? '— nao deu pra conferir agora (WhatsApp desconectado); da pra incluir assim mesmo'
                  : check.existe ? '✓ tem WhatsApp' : '× este numero nao tem WhatsApp'}
              </div>
            )}
            {checando && <div className="text-[11px] text-gray-500">conferindo no WhatsApp...</div>}

            {/* Botao desabilitado DIZ por que (convencao da casa): sem isto o operador fica
                parado num botao cinza sem saber que faltam digitos. */}
            <button
              type="button"
              onClick={incluirNovo}
              disabled={incluindo || novoTel.replace(/\D/g, '').length < 10}
              className="w-full py-1.5 rounded-lg btn-primario flex items-center justify-center gap-1.5 disabled:opacity-50 text-sm"
            >
              {incluindo ? <Loader2 size={14} className="animate-spin" /> : <UserPlus size={14} />}
              {incluindo ? 'Incluindo...' : 'Incluir no envio'}
            </button>
            {novoTel.replace(/\D/g, '').length > 0 && novoTel.replace(/\D/g, '').length < 10 && (
              <div className="text-[11px] text-gray-500">Digite o numero com DDD.</div>
            )}
          </div>

          <div className={temPreview ? 'flex flex-col min-h-0 flex-1' : undefined}>
            <label className="block text-xs font-semibold text-gray-600 mb-1 uppercase shrink-0">
              Marque quem vai receber
            </label>
            <div className="relative">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                value={texto}
                onChange={(e) => onChangeTexto(e.target.value)}
                onKeyDown={teclado.onKeyDown}
                placeholder="Digite o nome ou o numero..."
                autoFocus
                className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg outline-none focus:ring-2 focus:ring-green-500"
              />
              {buscando && (
                <Loader2 size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 animate-spin" />
              )}
            </div>

            {/* Quem esta marcado, sempre visivel — a lista abaixo troca com a busca, isto nao. */}
            {escolhidos.length > 0 && (
              <div className="mt-2 flex flex-wrap items-center gap-1.5">
                <span className="text-[11px] font-bold uppercase tracking-wide text-gray-500">
                  Vao receber ({escolhidos.length}):
                </span>
                {escolhidos.map((e) => (
                  <span
                    key={e.chave}
                    className={`inline-flex items-center gap-1 rounded-full pl-2.5 pr-1 py-0.5 text-xs font-medium ${
                      falhas.has(e.digitos) ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-800'
                    }`}
                  >
                    <span className="truncate max-w-[140px]">{e.nome}</span>
                    {falhas.has(e.digitos) && <span className="text-[10px] font-bold" title={falhas.get(e.digitos)}>falhou</span>}
                    <button
                      type="button"
                      onClick={() => alternar(e)}
                      disabled={enviando}
                      className="p-0.5 rounded-full hover:bg-black/10 disabled:opacity-50"
                      aria-label={`Desmarcar ${e.nome}`}
                    >
                      <X size={12} />
                    </button>
                  </span>
                ))}
              </div>
            )}

            {/* A lista. Campo vazio = cadastro do cliente + conversas recentes; digitou nome = a
                busca; digitou numero = a linha do proprio numero. Tudo com caixa de marcacao. */}
            {/* Com previa a lista ocupa o QUE SOBRA da coluna (flex-1), medido em runtime — nao um
                teto cravado, que sobraria buraco numa janela alta e rolaria numa baixa. Sem previa
                segue o `max-h-[280px]` de sempre. */}
            <div ref={teclado.listaRef} className={`mt-2 border border-gray-200 rounded-lg overflow-y-auto ${temPreview ? 'flex-1 min-h-0' : 'max-h-[280px]'}`}>
                {listaVisivel.length === 0 ? (
                  <div className="px-3 py-4 text-sm text-gray-500 text-center">
                    {carregandoRecentes && !texto.trim()
                      ? 'Carregando conversas recentes...'
                      : texto.trim().length >= 2
                        ? 'Nenhum contato encontrado. Digite o numero com DDD pra marcar ele.'
                        : 'Nenhuma conversa recente. Digite o nome ou o numero.'}
                  </div>
                ) : (
                  listaVisivel.map((d, idx) => {
                    const marcado = marcados.has(chaveTel(d.digitos));
                    // Amarelo = e o numero do CADASTRO do cliente. Nao diz nada sobre envio (isso e
                    // a caixa de marcacao) — e o cursor do teclado usa anel, nao fundo, justamente
                    // pra nao voltar a competir com este amarelo (ver principio 3 do CLAUDE.md).
                    const doCadastro = d.chave.startsWith('cli-');
                    const curto = d.digitos.length < 10;
                    const motivoFalha = falhas.get(d.digitos);
                    // Cabecalho de secao: so quando a lista e a de recentes (campo vazio).
                    const semBusca = texto.trim().length < 2;
                    const cabecalho = !semBusca ? null
                      : idx === 0 && numerosCliente.length > 0 ? 'Cliente do orcamento'
                      : idx === numerosCliente.length ? 'Conversas recentes'
                      : null;
                    return (
                      <div key={d.chave}>
                        {cabecalho && (
                          <div className="px-3 pt-2 pb-1 text-[11px] font-bold uppercase tracking-wide text-gray-500 bg-gray-50 border-b border-gray-100">
                            {cabecalho}
                          </div>
                        )}
                        <button
                          type="button"
                          data-idx={idx}
                          role="checkbox"
                          aria-checked={marcado}
                          disabled={curto || enviando}
                          onClick={() => alternar(d)}
                          onMouseEnter={() => teclado.setHighlight(idx)}
                          onDoubleClick={() => { if (!curto) void enviar(d.digitos); }}
                          className="w-full flex items-center gap-3 px-3 py-2 text-left border-b border-gray-100 last:border-b-0 disabled:opacity-60 disabled:cursor-not-allowed"
                          style={{
                            backgroundColor: doCadastro ? '#fde68a' : undefined,
                            ...(idx === teclado.highlight ? { outline: '2px solid var(--primary)', outlineOffset: '-2px' } : null),
                          }}
                        >
                          <span
                            className={`w-5 h-5 rounded flex items-center justify-center flex-shrink-0 ${marcado ? '' : 'border border-gray-400 bg-white'}`}
                            style={marcado ? { backgroundColor: 'var(--primary)' } : undefined}
                          >
                            {marcado && <Check size={14} className="text-white" strokeWidth={3} />}
                          </span>
                          {d.foto_url ? (
                            <img src={d.foto_url} alt="" className="w-9 h-9 rounded-full object-cover flex-shrink-0" />
                          ) : (
                            <div className="w-9 h-9 rounded-full bg-gray-200 flex items-center justify-center text-sm font-semibold text-gray-600 flex-shrink-0">
                              {/* 1o caractere ALFANUMERICO: a linha do numero digitado tem nome
                                  "(64) 9...", e `charAt(0)` desenhava um parenteses no avatar. */}
                              {(d.nome.match(/[a-zA-Z0-9]/)?.[0] ?? '?').toUpperCase()}
                            </div>
                          )}
                          <div className="flex-1 min-w-0">
                            <div className={`text-sm font-medium truncate ${doCadastro ? 'text-black' : 'text-gray-800'}`}>
                              {d.nome}
                            </div>
                            <div className={`text-xs ${doCadastro ? 'text-black' : 'text-gray-500'}`}>
                              {exibirTelefone(d.digitos)}
                              {d.rotulo && <span className="ml-2">· {d.rotulo}</span>}
                            </div>
                            {motivoFalha && <div className="text-xs font-medium text-red-700 mt-0.5">Não enviado: {motivoFalha}</div>}
                          </div>
                          {d.quando && (
                            <div className={`text-[11px] flex-shrink-0 ${doCadastro ? 'text-black' : 'text-gray-500'}`}>
                              {quandoCurto(d.quando)}
                            </div>
                          )}
                        </button>
                      </div>
                    );
                  })
                )}
            </div>

            <p className="text-[11px] text-gray-500 mt-2 shrink-0">
              {fly
                ? 'Sera enviado o fly, com o preco deste orcamento. O PDF nao vai.'
                : imagensProntas && imagensProntas.length > 0
                ? 'Sera enviada a imagem gerada.'
                : 'Sera enviado um arquivo PDF do orcamento.'}
            </p>
          </div>
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-gray-200 bg-gray-50 rounded-b-xl shrink-0">
          <button
            onClick={() => onClose()}
            disabled={enviando}
            className="px-4 py-2 text-sm bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition disabled:opacity-50"
          >
            Cancelar
          </button>
          <button
            onClick={() => void enviar()}
            disabled={enviando || escolhidos.length === 0}
            className="px-5 py-2 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 transition disabled:bg-gray-200 disabled:text-gray-400 disabled:cursor-not-allowed inline-flex items-center gap-2"
          >
            {enviando ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
            {enviando ? 'Enviando...' : `Enviar${escolhidos.length > 0 ? ` (${escolhidos.length})` : ''}`}
          </button>
        </div>
      </div>

      {/* Simulacao em tamanho real. ⛔ NAO fecha por clique no fundo (convencao da casa) — so' pelo
          X, que fica grande e no canto porque no tablet nao ha Esc. */}
      {ampliada && temPreview && (
        <div className="absolute inset-0 bg-black/80 flex items-start justify-center z-[1200] p-6 overflow-y-auto">
          <div className="space-y-2">
            {imagensProntas!.map((img, i) => (
              <img
                key={i}
                src={`data:image/jpeg;base64,${img}`}
                alt={`Simulação — página ${i + 1}`}
                className="w-full max-w-[640px] rounded shadow-lg bg-white"
              />
            ))}
          </div>
          <button
            type="button"
            onClick={() => setAmpliada(false)}
            aria-label="Fechar a simulação ampliada"
            className="absolute top-3 right-4 w-10 h-10 rounded-full bg-white text-gray-800 text-2xl leading-none shadow-lg"
          >
            ×
          </button>
        </div>
      )}
    </div>
  );
}
